/**
 * Display labels and colors for sequence enrollment exit statuses.
 *
 * All terminal enrollments have status='completed'. The exit_reason
 * distinguishes outcomes:
 *   exit_reason=engaged       → "Engaged"
 *   exit_reason=timed_out     → "Timed Out"
 *   exit_reason=disqualified  → "Disqualified"
 *   exit_reason=bounced (outcome_reason) → "Bounced"
 *   exit_reason=unsubscribed (outcome_reason) → "Unsubscribed"
 *   exit_reason=* (other)     → "Removed"
 */

export type SequenceExitStatus = 'engaged' | 'timed_out' | 'disqualified'

export function sequenceExitLabel(status: string, exitReason?: string | null): string {
  if (status !== 'completed') return ''
  const reason = String(exitReason || '').trim().toLowerCase()
  if (reason === 'engaged') return 'Engaged'
  if (reason === 'timed_out') return 'Timed Out'
  if (reason === 'disqualified') return 'Disqualified'
  if (reason === 'bounced') return 'Bounced'
  if (reason === 'unsubscribed') return 'Unsubscribed'
  if (!reason) return ''
  return 'Removed'
}

export function sequenceExitColor(status: string, exitReason?: string | null): string {
  if (status !== 'completed') return '#6b7280'
  const reason = String(exitReason || '').trim().toLowerCase()
  if (reason === 'engaged') return '#10b981'    // green
  if (reason === 'timed_out') return '#f59e0b'  // amber
  if (reason === 'disqualified') return '#ef4444' // red
  if (reason === 'bounced') return '#ef4444'
  if (reason === 'unsubscribed') return '#6b7280'
  return '#6b7280' // grey
}

export interface EnrollmentStats {
  stepCounts: number[]
  stepCountsReady: number[]
  pendingExit: number
  exitedCompleted: number
  exitedTimedOut: number
  exitedDisqualified: number
}

/**
 * Aggregate a list of enrollment records (from /accounts/{id}/sequence-enrollments)
 * into display counts for SequenceSummary.
 *
 * Enrollments with needs_exit_decision=true are routed to pendingExit instead
 * of per-step counts so they appear in the virtual "Awaiting Exit" slot.
 */
export function buildEnrollmentStats(enrollments: any[]): EnrollmentStats {
  const stepCounts: number[] = []
  const stepCountsReady: number[] = []
  let pendingExit = 0
  let exitedCompleted = 0
  let exitedTimedOut = 0
  let exitedDisqualified = 0

  for (const e of enrollments) {
    if (e.status === 'active' || e.status === 'paused') {
      if (e.needs_exit_decision) {
        pendingExit++
      } else {
        const idx = Number(e.current_step_index ?? 0)
        if (e.ready_to_advance) {
          stepCountsReady[idx] = (stepCountsReady[idx] || 0) + 1
        } else {
          stepCounts[idx] = (stepCounts[idx] || 0) + 1
        }
      }
    } else if (e.status === 'completed') {
      if (e.exit_reason === 'engaged') exitedCompleted++
      else if (e.exit_reason === 'timed_out') exitedTimedOut++
      else exitedDisqualified++
    }
  }

  return { stepCounts, stepCountsReady, pendingExit, exitedCompleted, exitedTimedOut, exitedDisqualified }
}

/** Human-readable label for the three agent-selectable exit statuses */
export const EXIT_STATUS_OPTIONS: { value: SequenceExitStatus; label: string; description: string }[] = [
  {
    value: 'engaged',
    label: 'Engaged',
    description: 'Contact replied, showed interest, or participated in a call/meeting',
  },
  {
    value: 'timed_out',
    label: 'Timed Out',
    description: 'Sequence completed with no engagement from the contact',
  },
  {
    value: 'disqualified',
    label: 'Disqualified',
    description: 'Contact is not a good fit or should not be contacted further',
  },
]
